import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LocaleContext } from 'routes/Router';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row, Col, Stack } from 'react-bootstrap';
import { providerAdminCategory } from 'routes/AppUrls';
import { Editor } from 'react-draft-wysiwyg';
import { convertFromHTML, ContentState, EditorState } from 'draft-js';
import MuiBreadcrumbs from 'components/common/MuiBreadcrumbs';
import SwitchInput from 'components/common/Input/SwitchInput';
import LabelWithInput from 'components/common/Input/LabelWithInput';
import ToastNotification from 'components/common/Toast';
import SelectInput from 'components/common/Input/SelectInput';

import { getProviderSubCategoryCallAPI } from 'store/ProviderAdmin/getProviderSubCategory/action';
import { getCurrencyListCallAPI } from 'store/SuperAdmin/getCurrency/action';

import leftArrow from 'assets/images/Dashboard/arrow-left.png';
import ServiceLogo from 'assets/images/Dashboard/Logo.png';

const ProviderViewSubCategory = () => {
  const { id } = useParams();
  const { locale } = useContext(LocaleContext);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();

  const [conf, setConf] = ToastNotification();
  const [checked, setChecked] = useState(false);
  const [currencyData, setCurrencyData] = useState([]);

  const [breadcrumbsList, setBreadcrumbsList] = useState([
    {
      title: <FormattedMessage id="Service Categories" />,
      Link: providerAdminCategory(locale),
      isDisbale: false,
    },
    {
      title: <FormattedMessage id="View Sub-Category" />,
      Link: '',
      isDisbale: true,
    },
  ]);

  const [formValue, setFormValue] = useState({
    price: '',
    currency: '',
    timeRequired: '',
    subCategoryNameEn: '',
    subCategoryNameAr: '',
    afterCareTipsEn: '',
    afterCareTipsAr: '',
    benefitsEn: '',
    benefitsAr: '',
    precautionsEn: '',
    precautionsAr: '',
    procedureEn: '',
    procedureAr: '',
    productsUsedEn: '',
    productsUsedAr: '',
    thingsToKnowEn: '',
    thingsToKnowAr: '',
  });

  const [htmlValues, setHtmlValues] = useState({
    afterCareTipsEn: '',
    afterCareTipsAr: '',
    benefitsEn: '',
    benefitsAr: '',
    precautionsEn: '',
    precautionsAr: '',
    procedureEn: '',
    procedureAr: '',
    productsUsedEn: '',
    productsUsedAr: '',
    thingsToKnowEn: '',
    thingsToKnowAr: '',
  });

  const handleToastError = (res) => {
    setConf({
      msg: res?.data?.message,
      variant: 'error',
    });
  };

  const initializeEditorState = (htmlContent) => {
    if (htmlContent) {
      const blocksFromHTML = convertFromHTML(htmlContent);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  };

  useEffect(() => {
    getProviderSubCategory();
    getCurrencyList();
  }, []);

  const getProviderSubCategory = () => {
    dispatch(
      getProviderSubCategoryCallAPI(
        {},
        {
          run: true,
          id: id,
          handleFunction: (res) => handleSubCategoryData(res?.data?.data),
          toast: true,
          handleToastError: (res) => handleToastError(res),
          navigate: navigate,
        }
      )
    );
  };

  const getCurrencyList = () => {
    dispatch(
      getCurrencyListCallAPI(
        {},
        {
          run: true,
          handleFunction: (res) => setCurrencyData(res?.data?.data),
          toast: true,
          handleToastError: (res) => handleToastError(res),
          navigate: navigate,
        }
      )
    );
  };

  const handleSubCategoryData = (data) => {
    setFormValue((prev) => ({
      ...prev,
      price: data.price,
      currency: data.price_currency,
      timeRequired: data.time_required_in_mints,
      subCategoryNameEn: data.name_en,
      subCategoryNameAr: data.name_ar,
      afterCareTipsEn: data.after_care_tips_en,
      afterCareTipsAr: data.after_care_tips_ar,
      benefitsEn: data.benefits_en,
      benefitsAr: data.benefits_ar,
      precautionsEn: data.precautions_en,
      precautionsAr: data.precautions_ar,
      procedureEn: data.procedure_en,
      procedureAr: data.procedure_ar,
      productsUsedEn: data.material_used_en,
      productsUsedAr: data.material_used_ar,
      thingsToKnowEn: data.things_to_know_en,
      thingsToKnowAr: data.things_to_know_ar,
    }));

    setHtmlValues((prev) => ({
      ...prev,
      afterCareTipsEn: initializeEditorState(data.after_care_tips_en),
      afterCareTipsAr: initializeEditorState(data.after_care_tips_ar),
      benefitsEn: initializeEditorState(data.benefits_en),
      benefitsAr: initializeEditorState(data.benefits_ar),
      precautionsEn: initializeEditorState(data.precautions_en),
      precautionsAr: initializeEditorState(data.precautions_ar),
      procedureEn: initializeEditorState(data.procedure_en),
      procedureAr: initializeEditorState(data.procedure_ar),
      productsUsedEn: initializeEditorState(data.material_used_en),
      productsUsedAr: initializeEditorState(data.material_used_ar),
      thingsToKnowEn: initializeEditorState(data.things_to_know_en),
      thingsToKnowAr: initializeEditorState(data.things_to_know_ar),
    }));
  };

  const handleSwitchChange = (event) => {
    setChecked(!checked);
  };

  return (
    <Row className="bc-F2F3F6 m-0 p-0">
      <Col
        className={
          locale === 'en'
            ? 'commonEnContainer lg:ml-left'
            : 'commonArContainer lg:mr-right'
        }
      >
        <MuiBreadcrumbs size="14" list={breadcrumbsList} />
        <Row className="bc-FFFFFF m-0 p-16 mt-3">
          <div className="flex justify-between items-center">
            <h2 className="fc-292D32 fs-32 fw-400 ff-Akkurat_Bold m-0">
              <Stack direction="horizontal" gap={2} className="items-center">
                <img
                  src={leftArrow}
                  alt="icon"
                  style={{ height: '32px', width: '32px' }}
                  className="cursor-pointer"
                  onClick={() => navigate(providerAdminCategory(locale))}
                />
                <p className="p-0 m-0">
                  <FormattedMessage id="View Sub-Category" />
                </p>
              </Stack>
            </h2>
            <img
              src={ServiceLogo}
              alt={'logo'}
              style={{ height: '64px', width: '96px' }}
            />
          </div>

          <>
            <Row className="m-0 p-0">
              <div className="grid justify-end text-end">
                <SwitchInput
                  label={'En/Ar'}
                  checked={checked}
                  onChange={(e) => handleSwitchChange(e)}
                  color="secondary"
                />
              </div>
            </Row>

            {!checked ? (
              <>
                <Row className="m-0 p-0">
                  <Col xs={12} md={6} className="p-0">
                    <Row className="m-0 p-0">
                      <Col xs={12} md={12}>
                        <LabelWithInput
                          label={<FormattedMessage id="SubCategoryNameEn" />}
                          required={true}
                          placeholder={intl.formatMessage({
                            id: 'SubCategoryNameEn',
                          })}
                          name="subCategoryNameEn"
                          type="text"
                          value={formValue?.subCategoryNameEn}
                          disabled
                        />
                      </Col>

                      <Col xs={12} md={12} className="p-0">
                        <Row className="m-0 p-0">
                          <Col xs={12} md={3}>
                            <LabelWithInput
                              label={<FormattedMessage id="PriceEn" />}
                              required={true}
                              placeholder={intl.formatMessage({
                                id: 'PriceEn',
                              })}
                              name="price"
                              type="number"
                              value={formValue?.price}
                              disabled
                            />
                          </Col>

                          <Col xs={12} md={3}>
                            <SelectInput
                              label={<FormattedMessage id="CurrencyEn" />}
                              required={true}
                              placeholder={intl.formatMessage({
                                id: 'CurrencyEn',
                              })}
                              height="48"
                              name={'currency'}
                              value={formValue?.currency}
                              data={currencyData}
                              disabled
                            />
                          </Col>

                          <Col xs={12} md={6}>
                            <LabelWithInput
                              label={<FormattedMessage id="TimeRequiredEn" />}
                              required={true}
                              placeholder={intl.formatMessage({
                                id: 'TimeRequiredEn',
                              })}
                              name="timeRequired"
                              type="number"
                              value={formValue?.timeRequired}
                              disabled
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row className="m-0 p-0">
                  <Col xs={12} md={3} className="mt-3 pt-15">
                    <p className="p-0 m-0 fs-20 fc-292D32 fw-400 ff-Akkurat_Bold">
                      <FormattedMessage id="DescriptionEn" />
                    </p>
                  </Col>
                </Row>

                <>
                  <Col xs={12} md={12} className="mt-2">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="ProductsusedEn" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.productsUsedEn}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="BenefitsEn" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.benefitsEn}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="ProcedureEn" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.procedureEn}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="PrecautionsEn" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.precautionsEn}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="ThingstoknowEn" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.thingsToKnowEn}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="AftercaretipsEn" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.afterCareTipsEn}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                </>
              </>
            ) : (
              <>
                <Row className="m-0 p-0">
                  <Col xs={12} md={6} className="p-0">
                    <Row className="m-0 p-0">
                      <Col xs={12} md={12}>
                        <LabelWithInput
                          label={<FormattedMessage id="SubCategoryNameAr" />}
                          required={true}
                          placeholder={intl.formatMessage({
                            id: 'SubCategoryNameAr',
                          })}
                          name="subCategoryNameAr"
                          type="text"
                          value={formValue?.subCategoryNameAr}
                          disabled
                        />
                      </Col>

                      <Col xs={12} md={12} className="p-0">
                        <Row className="m-0 p-0">
                          <Col xs={12} md={3}>
                            <LabelWithInput
                              label={<FormattedMessage id="PriceAr" />}
                              required={true}
                              placeholder={intl.formatMessage({
                                id: 'PriceAr',
                              })}
                              name="price"
                              type="number"
                              value={formValue?.price}
                              disabled
                            />
                          </Col>

                          <Col xs={12} md={3}>
                            <SelectInput
                              label={<FormattedMessage id="CurrencyAr" />}
                              required={true}
                              placeholder={intl.formatMessage({
                                id: 'CurrencyAr',
                              })}
                              height="48"
                              name={'currency'}
                              value={formValue?.currency}
                              data={currencyData}
                              disabled
                            />
                          </Col>

                          <Col xs={12} md={6}>
                            <LabelWithInput
                              label={<FormattedMessage id="TimeRequiredAr" />}
                              required={true}
                              placeholder={intl.formatMessage({
                                id: 'TimeRequiredAr',
                              })}
                              name="timeRequired"
                              type="number"
                              value={formValue?.timeRequired}
                              disabled
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row className="m-0 p-0">
                  <Col xs={12} md={3} className="mt-3 pt-15">
                    <p className="p-0 m-0 fs-20 fc-292D32 fw-400 ff-Akkurat_Bold">
                      <FormattedMessage id="DescriptionAr" />
                    </p>
                  </Col>
                </Row>

                <>
                  <Col xs={12} md={12} className="mt-2">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="ProductsusedAr" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.productsUsedAr}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="BenefitsAr" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.benefitsAr}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="ProcedureAr" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.procedureAr}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="PrecautionsAr" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.precautionsAr}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="ThingstoknowAr" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.thingsToKnowAr}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={12} className="mt-4">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat">
                      <FormattedMessage id="AftercaretipsAr" />
                    </p>
                    <div className="mt-2">
                      <Editor
                        editorState={htmlValues?.afterCareTipsAr}
                        toolbar={{
                          options: ['inline', 'list', 'textAlign', 'history'],
                          inline: {
                            options: [
                              'bold',
                              'italic',
                              'underline',
                              'strikethrough',
                            ],
                          },
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        readOnly
                        toolbarHidden
                      />
                    </div>
                  </Col>
                </>
              </>
            )}
          </>
        </Row>
      </Col>
    </Row>
  );
};

export default ProviderViewSubCategory;
